import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = 'Giving with Purpose';
const DESCRIPTION =
    "How your faith transforms every contribution — practical ways for donors on a budget and young Christians to give and serve, even with limited resources.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/1-fellowship.jpg',
            datePublished: '2026-07-14',
            dateModified: '2026-07-14',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: { '@id': 'https://devotionfund.com/resources/giving-with-purpose', '@type': 'WebPage' },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: "Can I still give faithfully if I'm on a tight budget?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — generosity isn't measured by the size of the gift. A small recurring monthly donation you can comfortably sustain often does more good over time than a one-time gift that strains your budget. Donating goods, volunteering a professional skill, or hosting a donation drive are equally valid ways to give when cash is tight.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'What if I have no money at all to donate?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'You can still give meaningfully. Volunteer your time or a professional skill (accounting, design, social media), donate unused household goods or clothing, or organize a collection drive with your church community. Prayer for the ministries and people you care about is also a real and powerful form of giving.',
                    },
                },
                {
                    '@type': 'Question',
                    name: "What's a good first step to start giving regularly?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Set up a small recurring monthly gift to one cause you care about, at an amount you can sustain without strain. Consistency compounds — a modest monthly gift adds up to more support over a year than a single larger gift you can't repeat.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How can young Christians get involved in missions without much money?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Look for local volunteer opportunities at Christian organizations, soup kitchens, or youth programs, or join a short-term mission trip — many of these offer fundraising support to help cover costs. Mentoring younger believers or offering tech skills to a church or ministry are also high-impact, low-cost ways to serve.',
                    },
                },
            ],
        },
    ],
};

export default function GivingWithPurpose() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/giving-with-purpose' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-giving-with-purpose-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/1-fellowship.jpg" alt="Giving with Purpose" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">Giving with Purpose: How Your Faith Transforms Every Contribution</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>Following Christ often involves a desire to give back, to share our blessings and contribute to something larger than ourselves. But what if your wallet feels lighter than your spirit? Fear not! There are countless ways to show your faith and make a difference, even if your financial resources seem limited.</p>
                    <p><strong>This blog post is for two groups:</strong></p>
                    <ul>
                        <li><strong>Donors:</strong> Those who want to faithfully donate but might be on a tight budget.</li>
                        <li><strong>Young Christians:</strong> Looking for ways to volunteer and get involved in Christian missions or organizations.</li>
                    </ul>
                    <p><strong>For Donors on a Budget:</strong></p>
                    <ul>
                        <li><strong>The Power of Small Amounts:</strong> Remember, every bit counts! Even a small monthly donation can add up significantly for a cause you care about. Consider setting up a recurring donation for a set amount you can comfortably manage.</li>
                        <li><strong>Think Outside the Cash Donation:</strong> Perhaps you have unused household items, furniture, or clothing a ministry could use. Donations of goods can be a huge blessing.</li>
                        <li><strong>Volunteer Your Skills:</strong> Do you have a talent for accounting, graphic design, or social media management? Many organizations welcome volunteers who can donate their professional skills.</li>
                        <li><strong>Host a Donation Drive:</strong> Rally your friends, family, or church community and organize a collection of food, clothing, school supplies, or other needed items.</li>
                    </ul>
                    <p><strong>For Young Christians Who Want to Volunteer:</strong></p>
                    <ul>
                        <li><strong>Seek Out Volunteer Opportunities:</strong> Look for local Christian organizations, soup kitchens, homeless shelters, or after-school programs that need your help.</li>
                        <li><strong>Consider a Mission Trip:</strong> Many organizations offer short-term mission trips focused on construction, teaching, or medical aid. Often, fundraising options are available to help cover costs.</li>
                        <li><strong>Donate Blood:</strong> This is a simple yet powerful way to serve others.</li>
                        <li><strong>Mentor Younger Children or Teens:</strong> Share your faith and knowledge by volunteering with youth groups or after-school programs.</li>
                        <li><strong>Offer Technical Skills:</strong> Are you tech-savvy? Help churches or ministries update their websites or social media presence.</li>
                    </ul>
                    <p><strong>Remember</strong>: Regardless of the amount you give or the time you volunteer, your act of service makes a difference. Giving back isn't about how much you have, but about the generosity of your spirit.</p>
                    <p><strong>Bonus Tip</strong>: Prayer is a powerful form of giving! Pray for the organizations you support and the individuals they serve. By following these tips, you can find ways to express your faith and make a positive impact, regardless of your budget. Let your light shine brightly!</p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/giving-with-purpose" />
                </div>
            </div>
            <Footer />
        </div>
    );
}


